import { useEffect, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useUserSetting } from '../../contexts/UserSettingsContext.jsx';

import './JxTooltip.css';

const jxTooltipSx = {
    tooltip: { className: 'jx-tooltip' },
    // Jx panels render at z-index 10000 — the MUI default popper z-index (1500)
    // would render BEHIND them, making the tooltip invisible. Force it above.
    popper: { className: 'jx-tooltip-popper' },
};

// Extra afstand tussen anchor/cursor en tooltip (2026-07-09) — MUI's default
// zit standaard vlak tegen het anchor-element (of, bij followCursor, vlak
// tegen de cursor) aan, niet gewenst voor een subtiele hint. offset-modifier
// op de popper duwt 'm verder weg. followCursor krijgt een groter offset dan
// een normale anchor-tooltip (een kleine 14x14 handle heeft al een beetje
// natuurlijke ruimte door zijn eigen afmeting, de cursor zelf heeft dat niet).
const popperPropsByMode = {
    followCursor: { modifiers: [{ name: 'offset', options: { offset: [0, 20] } }] },
    anchored: { modifiers: [{ name: 'offset', options: { offset: [0, 10] } }] },
};

/**
 * JxTooltip — themed tooltip, wraps MUI Tooltip with Jx styling.
 * Restored 2026-07-09 (was .deprecated) — zie Development.Patterns
 * rk=91877cd246fa4653822093502e086c45 (JxTooltip refactor slag die compleet
 * zijn punt mist): de fout was de abstractie zelf te deprecaten i.p.v. 'm
 * intern te blijven aanpassen. Content (title) mag gewoon JSX zijn, bv.
 * meerdere <JxTooltipRowItemDescription>-rijen i.p.v. platte tekst.
 *
 * GEEN MUI arrow-prop meer (2026-07-09, na 3 mislukte pogingen om de
 * ingebouwde arrow-span groter te krijgen — zie JxTooltip.css voor de
 * volledige uitleg). De callout-punt is nu een eigen ::after op de
 * .jx-tooltip-box zelf (CSS), volledig los van MUI's arrow-mechanisme.
 *
 * @param {React.ReactNode} title
 * @param {React.ReactNode} children
 * @param {string} placement
 * @param {boolean} followCursor - tooltip volgt de cursor i.p.v. vast bij het
 *   anchor-element te hangen. Nodig voor UmlConnectorLayer.jsx (hint op een
 *   lange, dunne connector-lijn — vast bij het element zou vaak ver van de
 *   cursor af belanden). Beide modi krijgen automatisch een offset (zie
 *   popperPropsByMode hierboven) zodat de tooltip niet bovenop de
 *   cursor/anchor plakt.
 */
export default function JxTooltip({ title, children, placement = 'top', followCursor = false }) {
    const enabled = useUserSetting('tooltip.enabled');
    const durationMs = useUserSetting('tooltip.durationMs');
    const [open, setOpen] = useState(false);
    const hideTimerRef = useRef(null);

    useEffect(() => {
        return function clearHideTimerOnUnmount() {
            clearTimeout(hideTimerRef.current);
        };
    }, []);

    if (!title || enabled === false) return children;

    /**
     * Opens the tooltip and schedules an auto-hide — a hint you already know
     * shouldn't keep sitting on screen just because the cursor hasn't moved.
     */
    function handleOpen() {
        setOpen(true);
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(function autoHideTooltip() { setOpen(false); }, durationMs);
    }

    function handleClose() {
        clearTimeout(hideTimerRef.current);
        setOpen(false);
    }

    return (
        <Tooltip
            title={title}
            placement={placement}
            disableInteractive
            followCursor={followCursor}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            slotProps={{
                popper: { ...jxTooltipSx.popper, ...(followCursor ? popperPropsByMode.followCursor : popperPropsByMode.anchored) },
                tooltip: jxTooltipSx.tooltip,
            }}
        >
            {children}
        </Tooltip>
    );
}
